import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { DashboardHeader } from '../../pages/dashboard/components/DashboardHeader';
import { DashboardSidebar } from '../../pages/dashboard/components/DashboardSidebar';
import { MobileNav } from '../../pages/dashboard/components/MobileNav';

const lightTheme = {
    '--brand': '#2563EB',
    '--brand-hover': '#1D4ED8',
    '--brand-light': '#3B82F6',
    '--brand-dim': 'rgba(37,99,235,0.09)',
    '--brand-dim-md': 'rgba(37,99,235,0.14)',
    '--brand-border': 'rgba(37,99,235,0.22)',
    '--status-up': '#10B981',
    '--status-up-bg': 'rgba(16,185,129,0.10)',
    '--status-up-text': '#059669',
    '--status-down': '#EF4444',
    '--status-down-bg': 'rgba(239,68,68,0.10)',
    '--status-down-text': '#DC2626',
    '--status-warn': '#F59E0B',
    '--status-warn-bg': 'rgba(245,158,11,0.10)',
    '--status-warn-text': '#D97706',
    '--cash': '#10B981',
    '--noncash': '#2563EB',
    '--bg': '#F8FAFC',
    '--bg-elevated': '#FFFFFF',
    '--bg-hover': '#F1F5F9',
    '--border': 'rgba(0,0,0,0.07)',
    '--border-strong': 'rgba(0,0,0,0.12)',
    '--text': '#0F172A',
    '--text-secondary': '#64748B',
    '--text-muted': '#94A3B8',
    '--shadow': '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)',
    '--shadow-lg': '0 10px 15px -3px rgba(0,0,0,0.08), 0 4px 6px -2px rgba(0,0,0,0.04)'
};

const darkTheme = {
    '--brand': '#3B82F6',
    '--brand-hover': '#2563EB',
    '--brand-light': '#60A5FA',
    '--brand-dim': 'rgba(59,130,246,0.15)',
    '--brand-dim-md': 'rgba(59,130,246,0.25)',
    '--brand-border': 'rgba(59,130,246,0.3)',
    '--status-up': '#34D399',
    '--status-up-bg': 'rgba(52,211,153,0.15)',
    '--status-up-text': '#6EE7B7',
    '--status-down': '#F87171',
    '--status-down-bg': 'rgba(248,113,113,0.15)',
    '--status-down-text': '#FCA5A5',
    '--status-warn': '#FBBF24',
    '--status-warn-bg': 'rgba(251,191,36,0.15)',
    '--status-warn-text': '#FCD34D',
    '--cash': '#34D399',
    '--noncash': '#60A5FA',
    '--bg': '#0F1117',
    '--bg-elevated': '#181C27',
    '--bg-hover': '#1E2333',
    '--border': 'rgba(255,255,255,0.07)',
    '--border-strong': 'rgba(255,255,255,0.12)',
    '--text': '#E8EAF0',
    '--text-secondary': '#7A7F96',
    '--text-muted': '#475569',
    '--shadow': '0 1px 3px rgba(0,0,0,0.3), 0 1px 2px rgba(0,0,0,0.2)',
    '--shadow-lg': '0 10px 15px -3px rgba(0,0,0,0.4), 0 4px 6px -2px rgba(0,0,0,0.3)'
};

const getInitialTheme = (): boolean => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
};

export const MainLayout: React.FC = () => {
    const [darkMode, setDarkMode] = useState(getInitialTheme);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    
    const navigate = useNavigate();
    const location = useLocation();

    const getActivePage = useCallback(() => {
        const path = location.pathname;
        if (path === '/' || path === '/dashboard') return 'dashboard';
        return path.split('/')[1] || 'dashboard';
    }, [location.pathname]);

    const activePage = getActivePage();

    useEffect(() => {
        const theme = darkMode ? darkTheme : lightTheme;
        Object.entries(theme).forEach(([key, value]) => {
            document.documentElement.style.setProperty(key, value);
        });
        document.documentElement.classList.toggle('dark', darkMode);
    }, [darkMode]);

    const handleNavigate = useCallback((page: string) => {
        const path = page === 'dashboard' ? '/dashboard' : `/${page}`;
        navigate(path);
        setMobileMenuOpen(false);
    }, [navigate]);

    const handleToggleTheme = useCallback(() => {
        setDarkMode(prev => !prev);
    }, []);

    const handleToggleMobileMenu = useCallback(() => {
        setMobileMenuOpen(prev => !prev);
    }, []);

    const handleCloseMobile = useCallback(() => {
        setMobileMenuOpen(false);
    }, []);

    return (
        <div className="flex flex-col h-screen overflow-hidden bg-[var(--bg)] text-[var(--text)] font-sans">
            <DashboardHeader
                darkMode={darkMode}
                onToggleTheme={handleToggleTheme}
                onNavigate={handleNavigate}
                onToggleMobileMenu={handleToggleMobileMenu}
            />

            <div className="flex flex-1 overflow-hidden relative">
                <DashboardSidebar
                    activePage={activePage}
                    onNavigate={handleNavigate}
                    mobileOpen={mobileMenuOpen}
                    onCloseMobile={handleCloseMobile}
                />

                <main className="flex-1 overflow-y-auto overflow-x-hidden pb-20 md:pb-0">
                    <Outlet />
                </main>
            </div>

            <MobileNav 
                activePage={activePage} 
                onNavigate={handleNavigate} 
            />
        </div>
    );
};